/**
 * SA Tools Platform - IPA Report Generator
 * Main platform component with 7-tab navigation
 */
import React, { useState, useEffect } from 'react';
import { Container, Box, Tabs, Title, Text } from '@mantine/core';
import { IconTool } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import Footer from '../../components/Footer';
import PlatformNavBar from '../../components/PlatformNavBar';
import { usePlatform } from '../../shared/contexts/PlatformContext';
import { defaultContext } from './utils/constants';

// Import tab components
import GettingStarted from './components/getting-started/GettingStarted';
import CustomerInfoSection from './components/context/CustomerInfoSection';
import ProposalTeamSection from './components/context/ProposalTeamSection';
import IPAContentSection from './components/context/IPAContentSection';
import ProcessTable from './components/process/ProcessTable';
import ROISection from './components/roi/ROISection';
import ReportDownloadPanel from './components/report/ReportDownloadPanel';
import aiService from './services/aiService';

function SaToolsApp({ onBackToPlatformSelector, onBackToHome, platformInfo }) {
  const { selectPlatform } = usePlatform();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [customerId, setCustomerId] = useState(null);
  const [contextData, setContextData] = useState(defaultContext);
  const [processes, setProcesses] = useState([]);

  // Set platform context on mount
  useEffect(() => {
    selectPlatform('satools', platformInfo);
  }, [selectPlatform, platformInfo]);

  // Load from sessionStorage on mount
  useEffect(() => {
    const savedCustomerId = sessionStorage.getItem('satools-customerId');
    if (savedCustomerId) {
      setCustomerId(savedCustomerId);

      const savedContext = sessionStorage.getItem(`satools-context-${savedCustomerId}`);
      if (savedContext) {
        try {
          setContextData(JSON.parse(savedContext));
        } catch (e) {
          console.error('Error parsing saved context:', e);
        }
      }

      const savedProcesses = sessionStorage.getItem(`satools-processes-${savedCustomerId}`);
      if (savedProcesses) {
        try {
          setProcesses(JSON.parse(savedProcesses));
        } catch (e) {
          console.error('Error parsing saved processes:', e);
        }
      }
    }
  }, []);

  // Save to sessionStorage when data changes
  useEffect(() => {
    if (customerId) {
      sessionStorage.setItem('satools-customerId', customerId);
      sessionStorage.setItem(`satools-context-${customerId}`, JSON.stringify(contextData));
      sessionStorage.setItem(`satools-processes-${customerId}`, JSON.stringify(processes));
    }
  }, [customerId, contextData, processes]);

  const handleFilesUploaded = ({ customerId: id, contextData: ctx, processes: procs }) => {
    const updates = [];

    if (id) setCustomerId(id);
    if (ctx) {
      setContextData(ctx); // Replace entire context, don't merge
      updates.push('customer context');
    }
    if (procs) {
      setProcesses(procs);
      updates.push(`${procs.length} process${procs.length !== 1 ? 'es' : ''}`);
    }

    const message = updates.length > 0
      ? `Loaded ${updates.join(' and ')} successfully!`
      : 'File uploaded successfully!';

    notifications.show({
      title: 'Success',
      message,
      color: 'green'
    });

    // Move to context tab
    setActiveTab('context');
  };

  const handleAIGenerateRatings = async (processList, context) => {
    if (!customerId) {
      notifications.show({
        title: 'Error',
        message: 'Please set up customer context first',
        color: 'red'
      });
      return;
    }

    try {
      notifications.show({
        title: 'AI Processing',
        message: 'Generating ratings for all processes...',
        color: 'blue',
        autoClose: false,
        id: 'ai-generate'
      });

      const result = await aiService.generateRatings(customerId, processList, context);

      if (result.processes) {
        setProcesses(result.processes);
        notifications.update({
          id: 'ai-generate',
          title: 'Success',
          message: `AI ratings generated for ${result.processCount} processes!`,
          color: 'green',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error generating AI ratings:', error);
      notifications.update({
        id: 'ai-generate',
        title: 'Error',
        message: `Failed to generate AI ratings: ${error.message}`,
        color: 'red',
        autoClose: 5000
      });
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container size="lg" style={{ flex: 1, padding: '20px' }}>
        {/* Header */}
        <Box mb="xl">
          <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconTool size={42} color="#7950f2" />
            <Box>
              <Title order={1} c="purple">
                SA Tools - IPA Report Generator
              </Title>
              <Text c="dimmed" size="sm">
                Interaction Process Automation Assessment and Report Generation
              </Text>
            </Box>
          </Box>

          {customerId && (
            <Text size="xs" c="purple" mt="xs" style={{ fontFamily: 'monospace' }}>
              Customer ID: <strong>{customerId}</strong> | Processes: <strong>{processes.length}</strong>
            </Text>
          )}

          {/* Top-left navigation: exit to platform selector + return to the SA Tools hub */}
          <PlatformNavBar
            onBackToPlatformSelector={onBackToPlatformSelector}
            onBackToHome={onBackToHome}
            showHome={!!onBackToHome}
            homeLabel="SA Tools"
            color="purple"
            mb={0}
          />
        </Box>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onChange={setActiveTab} color="purple">
          <Tabs.List>
            <Tabs.Tab value="getting-started">Getting Started</Tabs.Tab>
            <Tabs.Tab value="context">Customer Context</Tabs.Tab>
            <Tabs.Tab value="team">Proposal Team</Tabs.Tab>
            <Tabs.Tab value="content">IPA Content</Tabs.Tab>
            <Tabs.Tab value="processes">Process Assessment</Tabs.Tab>
            <Tabs.Tab value="roi">ROI</Tabs.Tab>
            <Tabs.Tab value="report">Report Generation</Tabs.Tab>
          </Tabs.List>

          {/* Tab Panels */}
          <Tabs.Panel value="getting-started" pt="xl">
            <GettingStarted onFilesUploaded={handleFilesUploaded} />
          </Tabs.Panel>

          <Tabs.Panel value="context" pt="xl">
            <CustomerInfoSection
              context={contextData}
              setContext={setContextData}
              customerId={customerId}
              setCustomerId={setCustomerId}
              processes={processes}
            />
          </Tabs.Panel>

          <Tabs.Panel value="team" pt="xl">
            <ProposalTeamSection
              context={contextData}
              setContext={setContextData}
            />
          </Tabs.Panel>

          <Tabs.Panel value="content" pt="xl">
            <IPAContentSection
              context={contextData}
              setContext={setContextData}
              customerId={customerId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="processes" pt="xl">
            <ProcessTable
              processes={processes}
              setProcesses={setProcesses}
              contextData={contextData}
              onAIGenerate={handleAIGenerateRatings}
            />
          </Tabs.Panel>

          <Tabs.Panel value="roi" pt="xl">
            <ROISection
              processes={processes}
              setProcesses={setProcesses}
              contextData={contextData}
            />
          </Tabs.Panel>

          <Tabs.Panel value="report" pt="xl">
            <ReportDownloadPanel
              customerId={customerId}
              contextData={contextData}
              processes={processes}
            />
          </Tabs.Panel>
        </Tabs>
      </Container>

      {/* Footer with purple theme */}
      <Footer platformColor="purple" />
    </Box>
  );
}

export default SaToolsApp;
